import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

import { getProfile, updProfile } from '@/controller/profile';
import FormField from '@/shared/components/UI/FormField';
import { toast } from '@/shared/components/UI/Toast';
import useCountry from '@/shared/hooks/useCountry';
import { useTranslations } from '@/translations/useTranslations.js';

const Profile = () => {
    // Form state
    const [formData, setFormData] = useState({
        title: '',
        firstname: '',
        lastname: '',
        email: '',
        dob: '',
        unit_no: '',
        street: '',
        postcode: '',
        state: '',
        mobile: {
            code: '+60',
            mobile_no: '',
        },
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const { t } = useTranslations();

    const [userID, setUserID] = useState(0);
    const [isMY, setisMY] = useState(true);

    const countryList = useCountry().getCountry();
    const countryPhoneList = useCountry().getCountryPhone();

    const [doRefresh, setRefresh] = useState(0);

    const [phoneRules, setPhoneRules] = useState([
        {
            required: true,
            message: 'Mobile Number is required',
        },
    ]);

    const titleOptions = [
        { value: 'mr', title: 'Mr.' },
        { value: 'ms', title: 'Ms.' },
        { value: 'mrs', title: 'Mrs.' },
    ];

    const states = [
        { value: 'johor', title: 'Johor' },
        { value: 'kedah', title: 'Kedah' },
        { value: 'kelantan', title: 'Kelantan' },
        { value: 'melaka', title: 'Melaka' },
        { value: 'negeri_sembilan', title: 'Negeri Sembilan' },
        { value: 'pahang', title: 'Pahang' },
        { value: 'perak', title: 'Perak' },
        { value: 'perlis', title: 'Perlis' },
        { value: 'pulau_pinang', title: 'Pulau Pinang' },
        { value: 'selangor', title: 'Selangor' },
        { value: 'terengganu', title: 'Terengganu' },
        { value: 'kuala_lumpur', title: 'Kuala Lumpur' },
        { value: 'putrajaya', title: 'Putrajaya' },
        { value: 'labuan', title: 'Labuan' },
        { value: 'sabah', title: 'Sabah' },
        { value: 'sarawak', title: 'Sarawak' },
    ];

    const handleFieldChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData((prev) => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value,
                },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }));
        }

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    // Add validation for required fields
    const validateForm = () => {
        const newErrors = {};
        // if (!formData.title) newErrors.title = 'Title is required';
        if (!formData.firstname) newErrors.firstname = 'First name is required';
        if (!formData.lastname) newErrors.lastname = 'Last name is required';
        if (!formData.email) newErrors.email = 'Email is required';
        // if (!formData.state) newErrors.state = 'State is required';
        if (!formData.postcode) newErrors.postcode = 'Postcode is required';
        // if (!formData.mobile.mobile_no) newErrors['mobile.mobile_no'] = 'Mobile number is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onFinish = async (e) => {
        e.preventDefault();

        // Validate before submitting
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const data = {
                title: formData.title,
                first_name: formData.firstname,
                last_name: formData.lastname,
                email: formData.email,
                country_code: formData.mobile.code,
                phone_no: formData.mobile.mobile_no,
                dob: formData.dob === '' || formData.dob === null ? null : dayjs(formData.dob).format('YYYY-MM-DD'),
                unit_no: formData.unit_no,
                street: formData.street,
                state: formData.state,
                postcode: formData.postcode,
                status: 'active',
            };
            const ret = await updProfile(userID, data);
            if (ret.status === 200) {
                toast.success('Update Successfully');
                setRefresh(doRefresh + 1);
            } else {
                toast.error('Update Failed. Please check input field');
            }
        } catch (e) {
            toast.error('Something went wrong.');
        }
        setLoading(false);
    };

    useEffect(() => {
        const getUserProfile = async () => {
            setLoading(true);
            try {
                const ret = await getProfile();
                if (ret.status === 200) {
                    const userData = ret.data.data[0];
                    setUserID(userData.id);
                    setFormData({
                        title: userData.title || '',
                        firstname: userData.first_name || '',
                        lastname: userData.last_name || '',
                        email: userData.email || '',
                        dob: userData.birthdate === null ? '' : userData.birthdate,
                        unit_no: userData.unit_no || '',
                        street: userData.street || '',
                        postcode: userData.postcode || '',
                        state: userData.state || '',
                        mobile: {
                            code: userData.country_code ?? '+60',
                            mobile_no: userData.phone_no || '',
                        },
                    });
                    onChgPhoneCode(userData.country_code ?? '+60');
                }
            } catch (e) {
                console.error('Something went wrong', e);
            }
            setLoading(false);
        };

        getUserProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doRefresh]);

    const onChgPhoneCode = (code) => {
        if (code === '+60') {
            setPhoneRules([
                {
                    required: true,
                    message: 'Mobile Number is required',
                },
                {
                    pattern: /^[1][0-9]{8,9}$/,
                    message: 'Invalid mobile number format.',
                },
            ]);
        } else {
            setPhoneRules([
                {
                    required: true,
                    message: 'Mobile Number is required',
                },
                {
                    pattern: /^[0-9]{0,20}$/,
                    message: 'Invalid mobile number format!',
                },
            ]);
        }
    };

    const onChgCountry = (code) => {
        setFormData((prev) => ({
            ...prev,
            state: '',
        }));
        if (code === 'MY') {
            setisMY(true);
        } else {
            setisMY(false);
        }
    };

    return (
        <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5 font-sans bg-white">
            <div className="flex flex-col w-full max-w-4xl py-5 flex-1">
                {/* Header Section */}
                <div className="flex flex-wrap justify-between gap-3 p-4 mb-6">
                    <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                        {t('label.my_profile')}
                    </p>
                </div>

                {/* Profile Form */}
                <form onSubmit={onFinish} className="space-y-4">
                    {/* Personal Information Section */}
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Title */}
                            <div className="md:col-span-1">
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.title')}
                                </label>
                                <select
                                    className={`w-full h-14 rounded-xl border ${errors.title ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors appearance-none cursor-pointer`}
                                    value={formData.title}
                                    onChange={(e) => handleFieldChange('title', e.target.value)}
                                >
                                    <option value="">{t('form.title')}</option>
                                    {titleOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.title}
                                        </option>
                                    ))}
                                </select>
                                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                            </div>

                            <div></div>

                            {/* First Name */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.firstname')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`w-full h-14 rounded-xl border ${errors.firstname ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                    value={formData.firstname}
                                    onChange={(e) => handleFieldChange('firstname', e.target.value)}
                                    placeholder={t('form.firstname')}
                                    maxLength={50}
                                />
                                {errors.firstname && <p className="text-red-500 text-sm mt-1">{errors.firstname}</p>}
                            </div>

                            {/* Last Name */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.lastname')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`w-full h-14 rounded-xl border ${errors.lastname ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                    value={formData.lastname}
                                    onChange={(e) => handleFieldChange('lastname', e.target.value)}
                                    placeholder={t('form.lastname')}
                                    maxLength={50}
                                />
                                {errors.lastname && <p className="text-red-500 text-sm mt-1">{errors.lastname}</p>}
                            </div>

                            {/* Date of Birth */}
                            <div className="md:col-span-1">
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.dob')}
                                </label>
                                <input
                                    type="date"
                                    className={`w-full h-14 rounded-xl border ${errors.dob ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                    value={formData.dob}
                                    onChange={(e) => handleFieldChange('dob', e.target.value)}
                                />
                                {errors.dob && <p className="text-red-500 text-sm mt-1">{errors.dob}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Address Information Section */}
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Unit No */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">Unit No.</label>
                                <input
                                    type="text"
                                    className={`w-full h-14 rounded-xl border ${errors.unit_no ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                    value={formData.unit_no}
                                    onChange={(e) => handleFieldChange('unit_no', e.target.value)}
                                    placeholder="Unit No."
                                    maxLength={20}
                                />
                                {errors.unit_no && <p className="text-red-500 text-sm mt-1">{errors.unit_no}</p>}
                            </div>

                            {/* Street */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">Street</label>
                                <input
                                    type="text"
                                    className={`w-full h-14 rounded-xl border ${errors.street ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                    value={formData.street}
                                    onChange={(e) => handleFieldChange('street', e.target.value)}
                                    placeholder="Street"
                                    maxLength={100}
                                />
                                {errors.street && <p className="text-red-500 text-sm mt-1">{errors.street}</p>}
                            </div>

                            {/* Postcode */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.postcode')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`w-full h-14 rounded-xl border ${errors.postcode ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors`}
                                    value={formData.postcode}
                                    onChange={(e) => handleFieldChange('postcode', e.target.value)}
                                    placeholder={t('form.postcode')}
                                    maxLength={10}
                                />
                                {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
                            </div>

                            {/* State */}
                            <div>
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.state')}
                                </label>
                                <select
                                    className={`w-full h-14 rounded-xl border ${errors.state ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors appearance-none cursor-pointer`}
                                    value={formData.state}
                                    onChange={(e) => handleFieldChange('state', e.target.value)}
                                >
                                    <option value="">{t('form.state')}</option>
                                    {states.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.title}
                                        </option>
                                    ))}
                                </select>
                                {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Contact Information Section */}
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Mobile Phone */}
                            <div className="md:col-span-2">
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    Mobile Phone Number
                                </label>
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 items-stretch">
                                    <select
                                        className="w-full sm:w-32 h-14 min-h-[3.5rem] rounded-xl sm:rounded-l-xl sm:rounded-r-none border sm:border-r-0 border-gray-300 bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors appearance-none cursor-pointer flex items-center"
                                        value={formData.mobile.code}
                                        onChange={(e) => {
                                            handleFieldChange('mobile.code', e.target.value);
                                            onChgPhoneCode(e.target.value);
                                        }}
                                    >
                                        {countryPhoneList.map((e, i) => (
                                            <option key={`flt${i}`} value={e.value}>
                                                {e.title}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="text"
                                        className={`flex-1 h-14 min-h-[3.5rem] rounded-xl sm:rounded-l-none sm:rounded-r-xl border ${errors['mobile.mobile_no'] ? 'border-red-500' : 'border-gray-300'} bg-white px-4 text-base outline-none focus:border-blue-500 transition-colors box-border`}
                                        value={formData.mobile.mobile_no}
                                        onChange={(e) => handleFieldChange('mobile.mobile_no', e.target.value)}
                                        placeholder="Mobile Number"
                                        maxLength={20}
                                    />
                                </div>
                                {errors['mobile.mobile_no'] && (
                                    <p className="text-red-500 text-sm mt-1">{errors['mobile.mobile_no']}</p>
                                )}
                            </div>

                            {/* Email */}
                            <div className="md:col-span-2">
                                <label className="block text-gray-900 text-base font-medium pb-2">
                                    {t('form.email')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="email"
                                    className="w-full h-14 rounded-xl border border-gray-300 bg-gray-100 px-4 text-base outline-none text-gray-500 cursor-not-allowed"
                                    value={formData.email}
                                    placeholder="example@email.com"
                                    readOnly
                                    disabled
                                />
                                <p className="text-gray-500 text-sm mt-1">Email address cannot be changed</p>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="p-4 flex justify-center">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                        >
                            <span className="overflow-hidden">
                                {loading ? 'Updating...' : t('label.update_my_profile')}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Profile;
